import React, { useState, useEffect, useCallback, useContext } from 'react'
import { useOutletContext } from 'react-router-dom'

// Assets e Componentes
import detailsIcon from '../../../../../assets/router/details.svg'
import LoadMoreButton from '../../../../../components/LoadMoreButton'
import { Filter } from '../../../../../components/SubHeaderFilter/types'

// Estilos
import {
  TrackingContainer,
  TableWrapper,
  StyledTable,
  Th,
  Td,
  TableRow,
  StatusText,
  IconCellButton,
  LoadMoreContainer,
  EmptyStateRow,
  MessageContainer, // <--- Importando o novo estilo
} from './style'

// API
import ApiService from '../../../../../services/ApiService'

// Modal
import ModalSteelOrder from '../ModalSteelOrder/modalsteel'

// Tipos
import { SteelOrder } from '../../SteelOrdersTable/types'
import UserContext from '../../../../../contexts/UserContext'

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`
const PAGE_SIZE = 50

type SteelOutletContext = {
  refresh: boolean
  appliedFilters: Filter[]
  search: string
}

// === HELPERS ===
const formatDate = (value?: string | Date) => {
  if (!value) return 'N/A'
  const date = new Date(value)
  if (isNaN(date.getTime())) return 'N/A'
  return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
}

const formatCurrency = (value?: number | string) => {
  if (value === undefined || value === null) return 'N/A'
  const num = Number(value)
  return isNaN(num)
    ? 'N/A'
    : num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const calculateStatus = (items: any[] | undefined) => {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return 'Aguardando'
  }

  const allFinished = items.every((i) => Number(i.status) === 2)
  if (allFinished) return 'Concluído'

  const hasInProgress = items.some(
    (i) => Number(i.status) === 1 || Number(i.status) === 2,
  )
  if (hasInProgress) return 'Em produção'

  return 'Aguardando'
}

const Tracking: React.FC = () => {
  const { refresh, appliedFilters, search } = useOutletContext<SteelOutletContext>()

  const [orders, setOrders] = useState<SteelOrder[]>([])
  const { user } = useContext(UserContext)

  const userRole = (user?.role as string)?.toUpperCase();
  const isTenantClient = userRole === 'TENANT_CLIENT';
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [hasMore, setHasMore] = useState(false)

  const [selectedOrder, setSelectedOrder] = useState<SteelOrder | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState(search)


  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)
    return () => clearTimeout(handler)
  }, [search])

  const fetchOrders = useCallback(
    async (pageNum: number, isRefresh: boolean = false) => {
      setIsLoading(true);
      try {
        const params: any = {
          page: pageNum,
          limit: PAGE_SIZE,
          tracking: true,
        };

        if (debouncedSearch && debouncedSearch.trim() !== '') {
          params.search = debouncedSearch;
        }

        if (appliedFilters && appliedFilters.length > 0) {
          params.filters = encodeURIComponent(JSON.stringify(appliedFilters));
        }


        const response = await ApiService.get(
          `${API_BASE_URL}/order/paginated`,
          params
        );

        const data = response.data?.data || [];
        const total = response.data?.total || 0;

        setOrders((prev) => (isRefresh ? data : [...prev, ...data]));
        setTotalItems(total);
        setHasMore(data.length === PAGE_SIZE && pageNum * PAGE_SIZE < total);
      } catch (error) {
        console.error('Erro ao buscar pedidos:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [appliedFilters, debouncedSearch]
  );

  useEffect(() => {
    setPage(1)
    fetchOrders(1, true)
  }, [refresh, fetchOrders, debouncedSearch, appliedFilters])

  const handleLoadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchOrders(nextPage, false)
  }

  // Handlers do Modal
  const handleOpenModal = (order: SteelOrder) => {
    setSelectedOrder(order)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedOrder(null)
  }

  // === CARREGAMENTO INICIAL COM MENSAGEM (IGUAL AO SEU EXEMPLO) ===
  if (isLoading && orders.length === 0) {
    return (
      <TrackingContainer>
        <MessageContainer>Carregando pedidos...</MessageContainer>
      </TrackingContainer>
    )
  }

  return (
    <TrackingContainer>
      <TableWrapper>
        <StyledTable>
          <thead>
            <tr>
              {!isTenantClient && <Th>Nº do pedido</Th>}
              <Th>NPC</Th>
              <Th>Pedido cliente</Th>
              <Th>Valor (R$)</Th>
              <Th>Data pedido</Th>
              <Th>Previsão entrega</Th>
              <Th>Status</Th>
              <Th>Qtd. de itens</Th>
              <Th style={{ width: '50px' }} />
            </tr>
          </thead>

          <tbody>
            {!isLoading && orders.length === 0 && (
              <EmptyStateRow>
                <Td colSpan={8}>Nenhum pedido encontrado.</Td>
              </EmptyStateRow>
            )}

            {orders.map((order) => {
              const deliveryDate = (order as any).deliveryDate
              const totalValue = (order as any).total
              const items = (order as any).items
              const status = calculateStatus(items)
              const itemsCount = items?.length || 0

              return (
                <TableRow key={order.id}>
                  {!isTenantClient && <Td>{order.saleNumberPloomes}</Td>}
                  <Td>{order.npc}</Td>
                  <Td>{(order as any).client ?? '--'}</Td>
                  <Td>{formatCurrency(totalValue)}</Td>
                  <Td>{formatDate((order as any).orderDate)}</Td>
                  <Td>{formatDate(deliveryDate)}</Td>

                  <Td>
                    <StatusText>{status}</StatusText>
                  </Td>

                  <Td>{itemsCount}</Td>

                  <Td>
                    <IconCellButton
                      type='button'
                      onClick={() => handleOpenModal(order)}
                      title='Ver Detalhes'
                    >
                      <img src={detailsIcon} alt='Detalhes' />
                    </IconCellButton>
                  </Td>
                </TableRow>
              )
            })}
          </tbody>
        </StyledTable>

        {(hasMore || (isLoading && orders.length > 0)) && (
          <LoadMoreContainer>
            <LoadMoreButton
              currentCount={orders.length}
              totalCount={totalItems}
              onLoadMore={handleLoadMore}
              isLoading={isLoading}
              hasMore={hasMore}
              label='pedidos'
            />
          </LoadMoreContainer>
        )}
      </TableWrapper>

      {/* COMPONENTE MODAL */}
      <ModalSteelOrder
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        order={selectedOrder}
      />
    </TrackingContainer>
  )
}

export default Tracking
